import React from 'react';
import type { ListingCardProps } from '../ListingCard';
export type SkeletonVariant = 'property' | 'apparel' | 'perfume' | 'food' | 'vehicle' | 'electronics' | 'furniture';

/** Which card a result will become — the placeholder takes that card's shape. */
export const skeletonVariantFor = (l: ListingCardProps): SkeletonVariant => l.dish ? 'food' : l.apparel ? 'apparel' : l.perfume ? 'perfume' : l.furniture ? 'furniture' : l.sector === 'property' ? 'property' : l.sector === 'electronics' ? 'electronics' : 'vehicle';
const Bar = ({
  className
}: {
  className: string;
}) => <div className={`rounded skeleton-block ${className}`} data-id="element-3397" data-mp="1r6gyc2mcml90" />;

/**
 * A placeholder in the shape of the card it will become. It is laid over the
 * real (still hidden) card, so its height always matches exactly; only the
 * inner anatomy changes by sector.
 */
export function ResultSkeleton({
  variant = 'vehicle'
}: {
  variant?: SkeletonVariant;
}) {
  return <div aria-hidden="true" className="h-full rounded-2xl bg-white border border-slate-100 shadow-sm overflow-hidden flex flex-col" data-id="element-3398" data-mp="zu884htt0dte">
      <div className="relative aspect-square skeleton-block" data-id="element-3399" data-mp="19re1jwpw4gws">
        <PhotoOverlay variant={variant} data-id="element-3400" data-mp="14rrmriueyslv" />
      </div>
      <div className="p-3 flex-1 flex flex-col gap-2" data-id="element-3401" data-mp="14px4ydefvgm0">
        <Body variant={variant} data-id="element-3402" data-mp="1iqrb0pe0l5hi" />
        <div className="mt-auto pt-2 flex items-center gap-3" data-id="element-3403" data-mp="1ipq14o8qip1d">
          <div className="h-9 flex-1 rounded-lg skeleton-block" data-id="element-3404" data-mp="1eq7ijz4dyq1p" />
          <Bar className="h-3 w-12" data-id="element-3405" data-mp="1env0llymofon" />
        </div>
      </div>
    </div>;
}

/** What each card carries on its photograph (badges, price, key facts). */
function PhotoOverlay({
  variant
}: {
  variant: SkeletonVariant;
}) {
  const chip = 'rounded-full bg-white/60';
  return <>
      <div className="absolute top-2 left-2 right-2 flex justify-between" data-id="element-3406" data-mp="22fi8elk77012">
        <div className={`h-5 w-12 ${chip}`} data-id="element-3407" data-mp="1xhgpq1qop2q4" />
        <div className={`h-5 w-24 ${chip}`} data-id="element-3408" data-mp="24jdk6u4pfr5k" />
      </div>
      {variant === 'property' && <div className="absolute bottom-2 left-2 right-2 flex gap-1.5" data-id="element-3409" data-mp="24hiyvepitx0y">
          <div className={`h-5 w-14 ${chip}`} data-id="element-3410" data-mp="29j005bbx3li2" />
          <div className={`h-5 w-14 ${chip}`} data-id="element-3411" data-mp="2eht9crk68qcf" />
          <div className={`h-5 w-16 ${chip}`} data-id="element-3412" data-mp="2efysppfxnke5" />
        </div>}
      {(variant === 'vehicle' || variant === 'electronics') && <div className="absolute bottom-2.5 left-2.5 space-y-1.5" data-id="element-3413" data-mp="2o7cxabmqrae5">
          <div className="h-5 w-24 rounded bg-white/60" data-id="element-3414" data-mp="2o735cii4yt5t" />
          <div className="h-2.5 w-16 rounded bg-white/50" data-id="element-3415" data-mp="2s86kf2sd0l5z" />
        </div>}
      {(variant === 'apparel' || variant === 'furniture') && <div className="absolute bottom-2 left-2 flex gap-1.5" data-id="element-3416" data-mp="2x8u6da1qebtc">
          {[0, 1, 2].map(i => <div key={i} className="w-5 h-5 rounded-full bg-white/60" data-id="element-3417" data-mp="326ci1874v551" />)}
        </div>}
      {variant === 'food' && <div className="absolute bottom-2 left-2 flex gap-1.5" data-id="element-3418" data-mp="17surrdfcfu6b">
          <div className={`h-5 w-16 ${chip}`} data-id="element-3419" data-mp="17v79bph6w2iu" />
          <div className={`h-5 w-10 ${chip}`} data-id="element-3420" data-mp="109twtgilz8uk" />
        </div>}
    </>;
}

/** The body of each card: name, price and the facts that sector leads with. */
function Body({
  variant
}: {
  variant: SkeletonVariant;
}) {
  const nameAndPrice = <div className="flex items-center justify-between gap-3" data-id="element-3421" data-mp="qk23epm8wlro">
      <Bar className="h-3.5 w-3/5" data-id="element-3422" data-mp="llqr888h9z6a" />
      <Bar className="h-3.5 w-14" data-id="element-3423" data-mp="lil0kok7ap8g" />
    </div>;
  switch (variant) {
    case 'property':
      // Property type + price, then community, then the nearby-school line.
      return <>
          {nameAndPrice}
          <Bar className="h-2.5 w-3/4" data-id="element-3424" data-mp="19a9xyjbvq3w9" />
          <Bar className="h-2.5 w-1/2" data-id="element-3425" data-mp="1998js5zkh1ab" />
        </>;
    case 'apparel':
      // Name + price, fabric, then the row of size chips.
      return <>
          {nameAndPrice}
          <Bar className="h-2.5 w-2/5" data-id="element-3426" data-mp="3u1k7clywd8mt" />
          <div className="flex gap-1.5 pt-0.5" data-id="element-3427" data-mp="3tyegp2amdyoz">
            {[0, 1, 2, 3, 4].map(i => <div key={i} className="h-7 w-8 rounded-md skeleton-block" data-id="element-3428" data-mp="3p038evzz3w8v" />)}
          </div>
        </>;
    case 'perfume':
      // House, name + price, notes, then the bottle-size segments.
      return <>
          <Bar className="h-2 w-1/4" data-id="element-3429" data-mp="17sa0p476h7wq" />
          {nameAndPrice}
          <Bar className="h-2.5 w-4/5" data-id="element-3430" data-mp="17px7tvzvtd4w" />
          <div className="flex gap-1.5 pt-0.5" data-id="element-3431" data-mp="1cql8l9dwx8cb">
            {[0, 1, 2].map(i => <div key={i} className="h-7 flex-1 rounded-md skeleton-block" data-id="element-3432" data-mp="20gyroaeirwur" />)}
          </div>
        </>;
    case 'food':
      // Dish + from-price, two lines of what's in it, the area.
      return <>
          {nameAndPrice}
          <Bar className="h-2.5 w-full" data-id="element-3433" data-mp="1qi9jxagc7ss8" />
          <Bar className="h-2.5 w-3/4" data-id="element-3434" data-mp="1vhcg05ecaoo2" />
          <Bar className="h-2 w-1/3" data-id="element-3435" data-mp="1vgb09nmdpe00" />
        </>;
    case 'furniture':
      // Name + price, type & material, the dimensions chip, lead time.
      return <>
          {nameAndPrice}
          <Bar className="h-2.5 w-3/5" data-id="element-3436" data-mp="k40iec96b2ac" />
          <div className="h-6 w-32 rounded-md skeleton-block" data-id="element-3437" data-mp="2ya86l93x41xe" />
          <Bar className="h-2 w-2/5" data-id="element-3438" data-mp="2y8nedq52sv2p" />
        </>;
    case 'vehicle':
    case 'electronics':
      // Name, then the three-spec strip, then the fourth spec.
      return <>
          <Bar className="h-3.5 w-2/3" data-id="element-3439" data-mp="37ad2on3nvdw9" />
          <div className="flex gap-2.5" data-id="element-3440" data-mp="339bb8sfzk64u">
            <Bar className="h-2.5 w-12" data-id="element-3441" data-mp="33acpt6w7narb" />
            <Bar className="h-2.5 w-14" data-id="element-3442" data-mp="337gqbe4pse0r" />
            <Bar className="h-2.5 w-10" data-id="element-3443" data-mp="2fixj71xei7a9" />
          </div>
          <Bar className="h-2 w-2/5" data-id="element-3444" data-mp="2fhcnh8zcoiat" />
        </>;
  }
}